import { useRef, useState } from 'react';
import Iconify from 'src/components/Iconify';
import { Badge } from 'src/@/components/ui/badge';
import { Button } from 'src/@/components/ui/button';
import { Switch } from 'src/@/components/ui/switch';
import { Textarea } from 'src/@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/@/components/ui/tooltip';

function slugify(str) {
    return str
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
}

function TagInput({ opcoes = [], onChange }) {
    const [inputVal, setInputVal] = useState('');
    const inputRef = useRef(null);

    const addTag = () => {
        const label = inputVal.trim();
        if (!label) return;
        const value = slugify(label) || label.toLowerCase();
        if (!value || opcoes.some((t) => t.value === value)) {
            setInputVal('');
            return;
        }
        onChange([...opcoes, { label, value }]);
        setInputVal('');
    };

    const removeTag = (tagValue) => {
        onChange(opcoes.filter((t) => t.value !== tagValue));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag();
        } else if (e.key === 'Backspace' && !inputVal) {
            onChange(opcoes.slice(0, -1));
        }
    };

    return (
        <div
            className="flex flex-wrap gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-xl min-h-[38px] cursor-text focus-within:border-[#7C3AED] focus-within:ring-1 focus-within:ring-[#7C3AED]/20 transition-colors"
            onClick={() => inputRef.current?.focus()}
        >
            {opcoes.map((tag) => (
                <span
                    key={tag.value}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-100 text-violet-700 text-xs font-medium rounded-full"
                >
                    {tag.label}
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeTag(tag.value); }}
                        className="hover:text-violet-900 leading-none"
                    >
                        <Iconify icon="eva:close-outline" width={12} />
                    </button>
                </span>
            ))}
            <input
                ref={inputRef}
                className="flex-1 min-w-[140px] text-sm outline-none bg-transparent placeholder:text-gray-400 py-0.5"
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={addTag}
                placeholder={opcoes.length === 0 ? 'Digite um canal e pressione Enter…' : ''}
            />
        </div>
    );
}

export default function QuestionRow({
    icon,
    titulo,
    descricao,
    state,
    essencialDisabled,
    qualificatoriaDisabled,
    onChange,
    onDelete,
    // SPIN-specific props. When spinField is set, this pergunta participates
    // in the Lais search-qualification flow and exposes an editable script.
    spinField,
    defaultScript,
    // Quando true, o item esta no catalogo so como referencia visual — o agente
    // nao tem extracao nem mapeamento SPIN, entao marcar essencial/qualificatoria
    // nao produz efeito. Renderizado com badge "Referencia" e nota explicativa.
    naoExtraivel = false,
    // Quando true, exibe um tag input para o admin inserir os canais livremente.
    opcoesConfiguraveis = false,
}) {
    const ativa = !!state?.ativa;
    const qualificatoria = !!state?.qualificatoria;
    const script = state?.script ?? '';
    const opcoes = state?.opcoes ?? [];
    const isSpin = Boolean(spinField);
    const [scriptOpen, setScriptOpen] = useState(false);

    return (
        <div className="py-3">
            <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] items-center gap-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gray-50 shrink-0">
                    <Iconify icon={icon} width={18} className="text-gray-600" />
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">{titulo}</p>
                        {isSpin && (
                            <Badge variant="outline" className="text-[10px] font-semibold uppercase tracking-wider text-violet-700 border-violet-200 bg-violet-50">
                                SPIN
                            </Badge>
                        )}
                        {naoExtraivel && (
                            <Badge variant="outline" className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 border-gray-200 bg-gray-50">
                                Referência
                            </Badge>
                        )}
                    </div>
                    {descricao && <p className="text-xs text-gray-500 line-clamp-2">{descricao}</p>}
                    {naoExtraivel && (
                        <p className="text-[11px] text-gray-400 italic mt-0.5">
                            Sem extração no agente — exibido apenas como referência.
                        </p>
                    )}
                </div>
                <div className="flex flex-col items-center gap-1">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span
                                tabIndex={0}
                                className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 inline-flex items-center gap-1 cursor-help outline-none"
                            >
                                Essencial
                                <Iconify icon="eva:info-outline" width={11} className="opacity-60" />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="center" className="max-w-xs text-xs leading-relaxed">
                            <p className="font-semibold mb-1">Essencial</p>
                            <p>
                                A Lais coleta essa informação naturalmente durante a conversa, no melhor momento. Se o lead já responder espontaneamente, ela não pergunta de novo.
                            </p>
                            <p className="mt-1.5 text-gray-300">
                                Ex.: <em>"Bairro de interesse"</em> marcado como essencial — a Lais pergunta apenas se o lead ainda não disse onde quer morar.
                            </p>
                        </TooltipContent>
                    </Tooltip>
                    <Switch
                        checked={ativa}
                        disabled={essencialDisabled}
                        onCheckedChange={(value) => onChange({ ativa: value })}
                    />
                </div>
                <div className="flex flex-col items-center gap-1">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span
                                tabIndex={0}
                                className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 inline-flex items-center gap-1 cursor-help outline-none"
                            >
                                Qualificatória
                                <Iconify icon="eva:info-outline" width={11} className="opacity-60" />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="center" className="max-w-xs text-xs leading-relaxed">
                            <p className="font-semibold mb-1">Qualificatória</p>
                            <p>
                                Obrigatória para qualificar o lead. Ele só é enviado ao CRM depois que essa pergunta for respondida. Só pode ser marcada em perguntas já definidas como Essenciais.
                            </p>
                            <p className="mt-1.5 text-gray-300">
                                Ex.: <em>"Orçamento"</em> como qualificatória — a Lais não conclui o atendimento sem entender quanto o lead pode investir.
                            </p>
                        </TooltipContent>
                    </Tooltip>
                    <Switch
                        checked={qualificatoria}
                        disabled={qualificatoriaDisabled}
                        onCheckedChange={(value) => onChange({ qualificatoria: value })}
                    />
                </div>
                {isSpin ? (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setScriptOpen((v) => !v)}
                        title={scriptOpen ? 'Fechar script' : 'Editar script'}
                        className={`rounded-xl ${scriptOpen ? 'text-[#7C3AED] bg-violet-50' : 'text-gray-400 hover:text-[#7C3AED] hover:bg-violet-50'}`}
                    >
                        <Iconify icon={scriptOpen ? 'eva:chevron-up-outline' : 'mdi:message-text-outline'} width={16} />
                    </Button>
                ) : (
                    <div className="w-10" />
                )}
                {onDelete ? (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={onDelete}
                        className="rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50"
                    >
                        <Iconify icon="eva:trash-2-outline" width={16} />
                    </Button>
                ) : (
                    <div className="w-10" />
                )}
            </div>
            {opcoesConfiguraveis && ativa && (
                <div className="mt-3 ml-[52px] mr-[52px] flex flex-col gap-1.5">
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Canais disponíveis{' '}
                        <span className="text-gray-400 normal-case tracking-normal">
                            — o agente oferecerá somente os adicionados
                        </span>
                    </label>
                    <TagInput
                        opcoes={opcoes}
                        onChange={(newOpcoes) => onChange({ opcoes: newOpcoes })}
                    />
                    {opcoes.length === 0 && (
                        <p className="text-[11px] text-amber-600">
                            Adicione ao menos um canal — sem opções o agente não saberá o que oferecer.
                        </p>
                    )}
                </div>
            )}
            {isSpin && scriptOpen && (
                <div className="mt-3 ml-[52px] mr-[52px] flex flex-col gap-1.5">
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Script da Lais <span className="text-gray-400 normal-case tracking-normal">(campo_faltante: {spinField})</span>
                    </label>
                    <Textarea
                        value={script}
                        onChange={(e) => onChange({ script: e.target.value })}
                        placeholder={
                            opcoesConfiguraveis && opcoes.length > 0
                                ? `${defaultScript}\n${opcoes.map((o) => `( ) ${o.label}`).join('\n')}`
                                : defaultScript || ''
                        }
                        rows={opcoesConfiguraveis && opcoes.length > 0 ? Math.min(opcoes.length + 2, 8) : 2}
                        className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
                    />
                    <p className="text-xs text-gray-400 whitespace-pre-wrap">
                        {'Padrão: '}
                        <em>
                            {opcoesConfiguraveis && opcoes.length > 0
                                ? `${defaultScript}\n${opcoes.map((o) => `( ) ${o.label}`).join('\n')}`
                                : defaultScript}
                        </em>
                    </p>
                </div>
            )}
        </div>
    );
}
